/** Everything the roster computes rather than stores. */
import { dayRange, daysBetween, expiryOf } from './dates'
import type {
  Grid,
  HseItem,
  HseStatus,
  HseValue,
  RosterCode,
  RosterDb,
} from './types'

/** Now-filter sentinels: the filter matches on these, never on the language-dependent label; no project name is spelled with angle brackets. */
export const NOW_FREE = '<free>'
export const NOW_TRAVEL = '<travel>'
export const NOW_LEAVE = '<leave>'
/** On site, not working, and NOT free: `DT` is `cat: 'work'` with no project, so `nowValue` and `freePeople` must agree on it. */
export const NOW_DOWNTIME = '<downtime>'

export function codeMap(codes: RosterCode[]): Map<string, RosterCode> {
  return new Map(codes.map((c) => [c.code, c]))
}

/**
 * A cell is a stack stored as ONE string, its codes joined by `+` in display order (`"pl+T"`); a parallel detail map would drift.
 * `+` is unambiguous because no code contains one. `codeAt` returns the LEADING code so the Now column, filters,
 * coverage and bus trips get one answer; only the grid and the code picker ask for the whole stack.
 */
export const STACK_SEP = '+'

export function stackOf(value: string | undefined): string[] {
  return (value ?? '').split(STACK_SEP).filter(Boolean)
}

export function joinStack(codes: string[]): string {
  return codes.filter(Boolean).join(STACK_SEP)
}

export function stackAt(grid: Grid, name: string, day: string): string[] {
  return stackOf(grid[name]?.[day])
}

export function codeAt(grid: Grid, name: string, day: string): string {
  return stackAt(grid, name, day)[0] ?? ''
}

/** Actual beats plan: a stale plan must not outrank a recorded fact. */
export function nowValue(db: RosterDb, name: string, day: string): string {
  const codes = codeMap(db.codes)
  const raw = codeAt(db.actual, name, day) || codeAt(db.plan, name, day)
  if (!raw) return NOW_FREE
  const c = codes.get(raw)
  if (!c) return NOW_FREE
  return nowOfCode(c)
}

/** What one known code says about where its holder is. */
export function nowOfCode(c: { cat: string; project: string }): string {
  if (c.cat === 'travel') return NOW_TRAVEL
  if (c.cat === 'leave') return NOW_LEAVE
  // Work with no project is downtime -- read from `cat` and `project` rather
  // than by naming the code `DT`, so a roster that adds a second standby code
  // gets the same answer without this function learning about it.
  if (!c.project) return NOW_DOWNTIME
  return c.project
}

/** One unbroken stretch of days with the same value; folding keeps a rotation to a handful of rows on the phone view. */
export interface ShiftRun {
  from: string
  to: string
  days: number
  value: string
  /** `value` resolved through `db.codes`; unknown codes drop out, as the grid draws them blank. */
  codes: RosterCode[]
  /** `none` is a genuinely empty stretch. */
  source: 'actual' | 'plan' | 'none'
}

/**
 * A person's days between two dates, folded into runs. Actual beats plan on the precedence `nowValue` uses,
 * so the phone header and list cannot disagree; a change of source breaks a run even with an identical code.
 */
export function runsFor(db: RosterDb, name: string, from: string, to: string): ShiftRun[] {
  const codes = codeMap(db.codes)
  const runs: ShiftRun[] = []
  for (const day of dayRange(from, to)) {
    const actual = db.actual[name]?.[day] ?? ''
    const value = actual || db.plan[name]?.[day] || ''
    const source: ShiftRun['source'] = actual ? 'actual' : value ? 'plan' : 'none'
    const last = runs.at(-1)
    if (last && last.value === value && last.source === source) {
      last.to = day
      last.days += 1
      continue
    }
    runs.push({
      from: day,
      to: day,
      days: 1,
      value,
      codes: stackOf(value)
        .map((code) => codes.get(code))
        .filter((c): c is RosterCode => Boolean(c)),
      source,
    })
  }
  return runs
}

/** Every project the schedule says a person works on across their FULL history; server-derived because the grids are bounded to the horizon. */
export function projectsDerived(db: RosterDb, name: string): string[] {
  return db.people.find((p) => p.name === name)?.projects ?? []
}

/** The Projects column: derived projects plus hand-made corrections, kept as a difference so a stored list cannot freeze. */
export function projectsOf(db: RosterDb, name: string): string[] {
  const person = db.people.find((p) => p.name === name)
  const derived = new Set(projectsDerived(db, name))
  for (const p of person?.projAdd ?? []) derived.add(p)
  for (const p of person?.projRem ?? []) derived.delete(p)
  return [...derived].sort((a, b) => a.localeCompare(b))
}

/** Head-count for a project x role on one day: the first period covering the day wins over base `req`, so ask per day, never per range. */
export function need(db: RosterDb, project: string, role: string, day: string): number {
  for (const period of db.reqP) {
    if (day >= period.from && day <= period.to) {
      return period.req[project]?.[role] ?? 0
    }
  }
  return db.req[project]?.[role] ?? 0
}

export function needAny(db: RosterDb, project: string, role: string): boolean {
  if (db.req[project]?.[role]) return true
  return db.reqP.some((p) => Boolean(p.req[project]?.[role]))
}

/** Planned days outside the horizon or with undefined codes; both are silent otherwise (a deleted code leaves grey cells). */
interface RosterWarning {
  kind: 'unknownCode' | 'outsideHorizon' | 'noRole'
  name: string
  day?: string
  detail?: string
}

export function warnings(db: RosterDb): RosterWarning[] {
  const codes = codeMap(db.codes)
  const roles = new Set(db.roles.map((r) => r.mn))
  const out: RosterWarning[] = []

  for (const person of db.people) {
    if (person.role && !roles.has(person.role)) {
      out.push({ kind: 'noRole', name: person.name, detail: person.role })
    }
  }
  for (const [gridName, grid] of [
    ['plan', db.plan],
    ['actual', db.actual],
  ] as const) {
    for (const [name, days] of Object.entries(grid)) {
      for (const [day, raw] of Object.entries(days)) {
        // each code of a stack is checked on its own; the raw `"pl+T"` would be reported as one unknown code
        const unknown = stackOf(raw).filter((code) => !codes.has(code))
        if (unknown.length) {
          out.push({
            kind: 'unknownCode',
            name,
            day,
            detail: `${unknown.join(STACK_SEP)} (${gridName})`,
          })
          // the horizon only applies to the planned grid: `actual` reaches back past it, and those filed shifts are neither wrong nor changeable
        } else if (gridName === 'plan' && (day < db.meta.start || day > db.meta.end)) {
          out.push({ kind: 'outsideHorizon', name, day, detail: gridName })
        }
      }
    }
  }
  // A single missing code can span hundreds of cells; the card is a warning,
  // not a log, so it is capped and the page says how many were hidden.
  return out
}


/** Only dated items with a renewal interval can expire; a blank is 'none', not 'expired', so the two problems are not coloured alike. */
export function hseStatus(item: HseItem, value: HseValue, asOf: string): HseStatus {
  if (value === null || value === undefined || value === '') return 'none'
  if (item.type !== 'date' || !item.months) return 'ok'
  const expires = expiryOf(String(value), item.months)
  if (!expires) return 'ok'
  if (expires < asOf) return 'expired'
  // A month's notice: long enough to order a replacement, short enough that
  // the column is not permanently amber.
  return daysBetween(asOf, expires) <= 30 ? 'due' : 'ok'
}

/** Coerce a pasted or typed cell to its column type; every write goes through here so fill, paste and typing land the same value. */
export function coerceHse(item: HseItem, raw: string): HseValue {
  const s = raw.trim()
  if (!s) return null
  switch (item.type) {
    case 'num': {
      const n = Number(s)
      return Number.isFinite(n) ? n : null
    }
    case 'check':
      return /^(1|true|yes|тийм|x|✓)$/i.test(s) ? true : null
    case 'yesno':
      return /^(1|true|yes|тийм)$/i.test(s) ? 'yes' : 'no'
    case 'date':
      // ISO only (the date input). Pasted text goes through `coercePasted` in `hsePaste.ts`; Excel's forms return null here and would clear the cell.
      return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null
    default:
      return s
  }
}


export function metres(from: number | '', to: number | ''): number | '' {
  if (from === '' || to === '') return ''
  const m = Number(to) - Number(from)
  return Number.isFinite(m) ? Math.round(m * 100) / 100 : ''
}

// Codes, roles, projects and companies are referenced only by string, with no FK, so deleting one must check who still names it.

export function codeInUse(db: RosterDb, code: string): number {
  let n = 0
  for (const days of Object.values(db.plan)) {
    for (const raw of Object.values(days)) {
      if (stackOf(raw).includes(code)) n += 1
    }
  }
  return n
}

/** People carrying this role (primary or secondary) plus project x period rows that still need it: `gapRows` keys on `role.mn`. */
export function roleInUse(db: RosterDb, roleMn: string): number {
  const people = db.people.filter((p) => p.roles.includes(roleMn)).length
  const reqRows = (req: Record<string, Record<string, number>>) =>
    Object.values(req).filter((byRole) => (byRole[roleMn] ?? 0) > 0).length
  const requirements = reqRows(db.req) + db.reqP.reduce((n, p) => n + reqRows(p.req), 0)
  return people + requirements
}

/** People with a value for this HSE item; deleting an item drops a grid column. A blank string counts as no value. */
export function hseItemInUse(db: RosterDb, itemId: string): number {
  return Object.values(db.hse.rec).filter((byItem) => {
    const value = byItem?.[itemId]
    return value !== undefined && value !== null && value !== ''
  }).length
}

/** The roster keys of projects Config has retired; null while the list is unknown, which hides nothing. */
export function retiredProjectKeys(
  faces: readonly { name_mn: string; on_roster: boolean; is_active: boolean }[] | null | undefined,
): ReadonlySet<string> | null {
  if (!faces) return null
  return new Set(faces.filter((f) => f.on_roster && !f.is_active && f.name_mn).map((f) => f.name_mn))
}

/** The codes of a closed project: drawn on the grid, never offered for a new cell. */
export function closedCodes<C extends { code: string; project: string }>(
  codes: readonly C[],
  closed: ReadonlySet<string> | null,
): ReadonlySet<string> {
  return new Set(codes.filter((c) => !!c.project && !!closed?.has(c.project)).map((c) => c.code))
}

/** The codes a list offers: a closed project's left out, unless `held` already shows one and it must still be named or taken off. */
export function pickerCodes<C extends { code: string; project: string }>(
  codes: readonly C[],
  closed: ReadonlySet<string> | null,
  held: Iterable<string> = [],
): C[] {
  const shut = closedCodes(codes, closed)
  const kept = new Set(held)
  return codes.filter((c) => !shut.has(c.code) || kept.has(c.code))
}

/** The closed project's codes that are drawn: no paint bar offers them, so only a key can name them. */
export function closedCodesDrawn<C extends { code: string; project: string }>(
  codes: readonly C[],
  closed: ReadonlySet<string> | null,
  drawn: Iterable<string>,
): C[] {
  const shut = closedCodes(codes, closed)
  const on = new Set(drawn)
  return codes.filter((c) => shut.has(c.code) && on.has(c.code))
}

/** Every code a grid draws. */
export function codesOnGrid(grid: Grid): Set<string> {
  const found = new Set<string>()
  for (const [name, days] of Object.entries(grid)) {
    for (const day of Object.keys(days)) for (const code of stackAt(grid, name, day)) found.add(code)
  }
  return found
}

/** Plan writes split by whether they would put a retired project's code into a cell that does not already hold that value. */
export function splitRetiredWrites<W extends { name: string; day: string; value: string }>(
  writes: readonly W[],
  plan: RosterDb['plan'] | undefined,
  retiredCodes: ReadonlySet<string>,
): { kept: W[]; refused: W[] } {
  const kept: W[] = []
  const refused: W[] = []
  for (const w of writes) {
    const adds = stackOf(w.value).some((code) => retiredCodes.has(code))
    ;(adds && (plan?.[w.name]?.[w.day] ?? '') !== w.value ? refused : kept).push(w)
  }
  return { kept, refused }
}

/** The first candidate project that is set and not retired, or ''. */
export function firstLiveProject(candidates: readonly (string | null | undefined)[], retired: ReadonlySet<string> | null): string {
  return candidates.find((p): p is string => !!p && !retired?.has(p)) ?? ''
}

/** A project list with Config-retired projects left out; `keep` names values a record already holds, so they stay readable. */
export function liveProjects(
  projects: readonly string[],
  retired: ReadonlySet<string> | null,
  keep: readonly (string | null | undefined)[] = [],
): string[] {
  if (!retired?.size) return [...projects]
  return projects.filter((p) => !retired.has(p) || keep.includes(p))
}
